// src/components/PrivacyPolicy.jsx
import { ShieldCheckIcon, EyeSlashIcon, LockClosedIcon, GlobeAltIcon } from '@heroicons/react/24/outline';

function PrivacyPolicy() {
  const lastUpdated = "June 24, 2025";

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-6 py-12 pt-32">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <ShieldCheckIcon className="w-16 h-16 text-purple-600" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Your privacy is our priority. We're committed to protecting your personal information and being transparent about how we use it.
            </p>
            <p className="text-sm text-gray-500 mt-4">Last updated: {lastUpdated}</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-xl shadow-sm p-8 space-y-8">
          
          {/* Trust Badges */}
          <div className="grid md:grid-cols-3 gap-6 p-6 bg-purple-50 rounded-lg">
            <div className="text-center">
              <EyeSlashIcon className="w-8 h-8 text-purple-600 mx-auto mb-2" />
              <p className="font-semibold text-gray-900">No Data Selling</p>
              <p className="text-sm text-gray-600">We never sell your personal information to third parties</p>
            </div>
            <div className="text-center">
              <LockClosedIcon className="w-8 h-8 text-purple-600 mx-auto mb-2" />
              <p className="font-semibold text-gray-900">Secure Storage</p>
              <p className="text-sm text-gray-600">Your data is encrypted and securely stored</p>
            </div>
            <div className="text-center">
              <GlobeAltIcon className="w-8 h-8 text-purple-600 mx-auto mb-2" />
              <p className="font-semibold text-gray-900">GDPR Compliant</p>
              <p className="text-sm text-gray-600">Full compliance with EU privacy regulations</p>
            </div>
          </div>

          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Introduction</h2>
            <p className="text-gray-700 leading-relaxed">
              Connect & Co ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our services. We believe in transparency and your right to understand how your data is handled.
            </p>
          </section>

          {/* Information We Collect */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Information We Collect</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Information You Provide</h3>
            <ul className="space-y-2 text-gray-700 mb-6">
              <li className="flex items-start">
                <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span><strong>Account Information:</strong> Email address, name, and other details you provide when signing up</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span><strong>Event Information:</strong> RSVP details, preferences, and participation data</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span><strong>Communications:</strong> Messages, feedback, and support requests you send us</span>
              </li>
            </ul>

            {/* NEW: Event Check-In and Email Collection Section */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
              <h3 className="text-xl font-semibold text-blue-800 mb-3">Event Check-In and Email Collection</h3>
              <p className="text-blue-700 mb-4 leading-relaxed">
                When you attend any Connect & Co event and check in (via QR code, manual check-in, or our mobile app), 
                we automatically add your email address to our community mailing list to enhance your experience with:
              </p>
              <ul className="space-y-2 text-blue-700 mb-4">
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span><strong>Event Notifications:</strong> Advance notice of upcoming events matching your interests and location</span>
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span><strong>Priority Access:</strong> Early RSVP opportunities for exclusive experiences</span>
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span><strong>Community Updates:</strong> Member highlights, platform news, and community spotlights</span>
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span><strong>Personalized Recommendations:</strong> Curated event suggestions based on your attendance history</span>
                </li>
              </ul>
              <div className="bg-blue-100 rounded-lg p-3">
                <p className="text-blue-800 text-sm">
                  <strong>Your Control:</strong> You can unsubscribe from our mailing list at any time using the unsubscribe link 
                  in any email we send, or by contacting us directly. Unsubscribing will not affect your ability to attend future events.
                </p>
              </div>
            </div>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Information We Collect Automatically</h3>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span><strong>Device Information:</strong> Browser type, operating system, screen resolution</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span><strong>Usage Data:</strong> Pages visited, time spent, click patterns (anonymized)</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span><strong>Location Data:</strong> General location (city/country) for event recommendations</span>
              </li>
            </ul>
          </section>

          {/* How We Use Your Information - UPDATED */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">How We Use Your Information</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="p-4 border border-gray-200 rounded-lg">
                <h3 className="font-semibold text-gray-800 mb-2">Service Provision</h3>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Send event invitations and updates</li>
                  <li>• Process event RSVPs and check-ins</li>
                  <li>• Provide customer support</li>
                  <li>• Facilitate community connections</li>
                  <li>• Send community mailing list communications</li>
                </ul>
              </div>
              <div className="p-4 border border-gray-200 rounded-lg">
                <h3 className="font-semibold text-gray-800 mb-2">Improvement & Security</h3>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Improve our platform and services</li>
                  <li>• Prevent fraud and abuse</li>
                  <li>• Analyze usage patterns</li>
                  <li>• Ensure platform security</li>
                  <li>• Personalize event recommendations</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Data Sharing - Emphasis on NOT selling */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Sharing & Disclosure</h2>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-green-800 mb-2">🚫 We Do NOT Sell Your Data</h3>
              <p className="text-green-700">
                We never sell, rent, or lease your personal information to third-party marketers, data brokers, or advertising companies. Your information stays with us.
              </p>
            </div>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">We Only Share Information When:</h3>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span><strong>Service Providers:</strong> With trusted partners who help us operate (email services, analytics) under strict data protection agreements</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span><strong>Legal Requirements:</strong> When required by law, court order, or to protect our rights</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span><strong>With Your Consent:</strong> When you explicitly authorize us to share specific information</span>
              </li>
            </ul>
          </section>

          {/* Your Rights (GDPR) */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Your Privacy Rights</h2>
            <p className="text-gray-700 mb-4">
              Under GDPR and other privacy laws, you have comprehensive rights regarding your personal data:
            </p>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-blue-50 rounded-lg">
                <h3 className="font-semibold text-blue-800 mb-2">Access & Portability</h3>
                <p className="text-sm text-blue-700">Request a copy of all data we have about you in a portable format</p>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg">
                <h3 className="font-semibold text-blue-800 mb-2">Rectification</h3>
                <p className="text-sm text-blue-700">Update or correct any inaccurate personal information</p>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg">
                <h3 className="font-semibold text-blue-800 mb-2">Erasure ("Right to be Forgotten")</h3>
                <p className="text-sm text-blue-700">Request deletion of your personal data</p>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg">
                <h3 className="font-semibold text-blue-800 mb-2">Object & Restrict</h3>
                <p className="text-sm text-blue-700">Object to processing or restrict how we use your data</p>
              </div>
            </div>

            <p className="text-gray-700 mt-4">
              To exercise these rights, contact us at <a href="mailto:privacy@connectco.app" className="text-purple-600 hover:text-purple-700">privacy@connectco.app</a>
            </p>
          </section>

          {/* Data Security */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Security</h2>
            <p className="text-gray-700 mb-4">
              We implement industry-standard security measures to protect your information:
            </p>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span>SSL/TLS encryption for all data transmission</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span>Encrypted data storage with secure cloud providers</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span>Regular security audits and updates</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span>Limited access controls and staff training</span>
              </li>
            </ul>
          </section>

          {/* International Transfers */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">International Data Transfers</h2>
            <p className="text-gray-700">
              As a global platform, your data may be processed in countries outside your home country. We ensure all international transfers comply with applicable privacy laws through Standard Contractual Clauses (SCCs) and other approved transfer mechanisms under GDPR.
            </p>
          </section>

          {/* Data Retention - UPDATED */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Retention</h2>
            <p className="text-gray-700 mb-4">
              We retain your personal information only as long as necessary to provide our services and comply with legal obligations:
            </p>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span><strong>Account Data:</strong> Deleted within 90 days of account closure, unless longer retention is required by law</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span><strong>Mailing List Data:</strong> Retained until you unsubscribe or delete your account; unsubscribe records kept to honor your preferences</span>
              </li>
            </ul>
          </section>

          {/* Children's Privacy */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Children's Privacy</h2>
            <p className="text-gray-700">
              Connect & Co is not intended for individuals under 18 years of age. We do not knowingly collect personal information from children under 18. If we discover we have collected such information, we will delete it immediately.
            </p>
          </section>

          {/* Contact */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Us</h2>
            <div className="bg-gray-50 rounded-lg p-6">
              <p className="text-gray-700 mb-4">
                If you have questions about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="space-y-2 text-gray-700">
                <p><strong>Email:</strong> <a href="mailto:privacy@connectco.app" className="text-purple-600 hover:text-purple-700">privacy@connectco.app</a></p>
                <p><strong>Subject Line:</strong> Privacy Policy Inquiry</p>
                <p><strong>Response Time:</strong> We respond to all privacy requests within 72 hours</p>
              </div>
            </div>
          </section>

          {/* Legal Disclaimer */}
           {/*
          <section className="border-t pt-6">
            <p className="text-sm text-gray-500 italic">
              <strong>Legal Note:</strong> This privacy policy is provided as a template and for informational purposes. It should be reviewed by qualified legal counsel to ensure compliance with applicable laws in your jurisdiction before implementation.
            </p>
          </section>
          */}
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;